import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { TokenService } from '../services/token.service';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';

/**
 * API Interceptor
 * Intercepts HTTP requests to:
 * - Add base URL
 * - Add authentication token
 * - Handle 401 errors with automatic token refresh
 * Based on atlas-frontend interceptor
 */
/** The only requests that carry no token: getting one, renewing it, checking it. */
const PUBLIC_ENDPOINTS = ['auth/login', 'auth/refresh', 'auth/verify'];

export const apiInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const tokenService = inject(TokenService);
  const router = inject(Router);
  const BASE_URL = environment.apiUrl;

  // If request is for external URL, send request without token
  if (request.url.indexOf('http') > -1) return next(request);

  // Direct request to API URL.
  let apiReq = request.clone({ url: `${BASE_URL}/${request.url}` });

  // Add trailing slash if it doesn't have one
  if (!apiReq.url.endsWith('/')) apiReq = apiReq.clone({ url: apiReq.url + '/' });

  // The token goes on everything EXCEPT the three endpoints that exist to get
  // one, renew it or check it. Named the other way round until 2026-09-24 —
  // "auth/ is public, except logout, me and users" — which meant every new
  // authenticated endpoint under auth/ silently went out without a token and
  // came back 401. `auth/supervisor_code/` was the first, and it looked like a
  // permissions bug for an hour.
  if (PUBLIC_ENDPOINTS.some((endpoint) => request.url.startsWith(endpoint))) return next(apiReq);

  // Add access token
  const access = tokenService.accessToken;
  if (access) {
    apiReq = apiReq.clone({
      headers: apiReq.headers.set('Authorization', `Bearer ${access}`),
    });
  }

  // Make the request
  return next(apiReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Throw error if not 401
      if (error.status !== 401) return throwError(() => error);
      console.log('Url:', apiReq.url);

      // Check if refresh token has expired
      return from(tokenService.isRefreshTokenExpired$()).pipe(
        switchMap((expired: boolean) => {
          // If refresh token expired, redirect to login
          if (expired) {
            tokenService.clearToken();
            router.navigate(['login']);
            return throwError(() => error);
          }

          // Refresh the token
          return from(tokenService.refreshToken$()).pipe(
            switchMap((success) => {
              // If couldn't refresh token, redirect to login
              if (!success) {
                tokenService.clearToken();
                router.navigate(['login']);
                return throwError(() => error);
              }

              // Build request with new access token
              const newAccess = tokenService.accessToken;
              const retryReq = apiReq.clone({
                headers: apiReq.headers.set('Authorization', `Bearer ${newAccess}`),
              });

              // Retry
              return next(retryReq);
            }),
          );
        }),
      );
    }),
  );
};
